import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetAccountStatusQuery } from '@services/account/account';
import {
  ACCOUNT_TYPE_MISMATCH_ERROR,
  GET_ACCOUNT_STATUS_DISABLED,
  NO_URL_FOUND_ERROR,
  POLLING_INTERVAL,
  POLLING_INTERVAL_END,
  SGA_POLLING_INTERVAL_END,
  GET_ACCOUNT_STATUS_FAILURE,
  GET_ACCOUNT_STATUS_URL_MISSING,
  ACCOUNT_CREATION_STATUS_TIMEOUT,
  GET_ACCOUNT_STATUS_TYPE_MISMATCH,
} from '@features/account-registration/constants';
import { AccountStatus } from '@models/account';
import isEqual from 'lodash/isEqual';
import { isWeblabLaunched } from 'src/shared/weblab/weblabUtils';
import { Weblab } from 'src/shared/weblab/enum';

const useAccountStatus = (
  onSuccessCallback: (regionalEntityId: string, status: AccountStatus) => void,
  onFailureCallback: (reason: string) => void,
) => {
  const [globalAccountId, setGlobalAccountId] = useState<string>('');
  const globalAccountStatus = useRef<AccountStatus>();
  // Redirect URL last seen on a PARTIALLY_CREATED poll, used by the timeout fallback.
  const partiallyCreatedUrl = useRef<string>('');
  const timerIdRef = useRef<ReturnType<typeof setTimeout>>();
  // Monotonic id incremented per polling run. A timer left over from a
  // superseded run compares its captured id against this and no-ops.
  const runIdRef = useRef<number>(0);
  // Exactly-once guard within a single run. The query-error effect, the
  // polling-window timeout, and a terminal response can each try to complete
  // the same run; the first to claim completion wins and the rest no-op, so
  // the caller never sees two contradictory terminal callbacks (e.g. a timeout
  // failure followed by a redirect).
  const completedRef = useRef<boolean>(false);
  const [isFetchingAccountStatus, setIsFetchingAccountStatus] =
    useState<boolean>(false);

  const url = window.location.search;
  const urlParams = new URLSearchParams(url);
  const managerAccountId = urlParams.get('entityId');

  // Poll the ownership-checked account-status endpoint directly. The creating
  // customer is authorized to read their own account while it is still PENDING
  // (GAMS createdBy fallback), and this endpoint returns the redirect URL once
  // the account reaches CREATED/PARTIALLY_CREATED.
  const { data: accountStatusData, isError: isAccountStatusError } =
    useGetAccountStatusQuery(
      {
        globalAccountId,
        managerAccountId: managerAccountId || undefined,
      },
      {
        pollingInterval: POLLING_INTERVAL,
        skip: !isFetchingAccountStatus,
      },
    );

  const isSingleGlobalAccountWeblabEnabled = isWeblabLaunched(
    Weblab.SINGLE_GLOBAL_ACCOUNT_LAUNCH,
  );

  const pollingIntervalLength = isSingleGlobalAccountWeblabEnabled
    ? SGA_POLLING_INTERVAL_END
    : POLLING_INTERVAL_END;

  const clearTimeOut = useCallback(() => {
    clearTimeout(timerIdRef.current);
  }, []);

  // Atomically claim completion of the current run. Returns true only for the
  // first caller; subsequent callers (competing completion paths) get false.
  // The winner also stops polling and cancels the window timer.
  const claimCompletion = useCallback(() => {
    if (completedRef.current) {
      return false;
    }
    completedRef.current = true;
    setIsFetchingAccountStatus(false);
    clearTimeout(timerIdRef.current);
    return true;
  }, []);

  // Cancel any pending polling-window timer on unmount.
  useEffect(() => () => clearTimeout(timerIdRef.current), []);

  useEffect(() => {
    if (isAccountStatusError && claimCompletion()) {
      onFailureCallback(GET_ACCOUNT_STATUS_FAILURE);
    }
  }, [isAccountStatusError, onFailureCallback, claimCompletion]);

  const pollAccountStatus = (globalAccountId: string) => {
    // Begin a new run: invalidate any prior run's timer/response, clear the
    // completion guard, and reset the per-run refs.
    runIdRef.current += 1;
    completedRef.current = false;
    clearTimeOut();
    globalAccountStatus.current = undefined;
    partiallyCreatedUrl.current = '';
    setGlobalAccountId(globalAccountId);
    setIsFetchingAccountStatus(!!globalAccountId);
    if (!globalAccountId) {
      return;
    }
    const runId = runIdRef.current;
    timerIdRef.current = setTimeout(() => {
      // Ignore a timer that belongs to a superseded run, or a run already
      // completed by a terminal response.
      if (runId !== runIdRef.current || !claimCompletion()) {
        return;
      }
      if (
        !isSingleGlobalAccountWeblabEnabled &&
        isEqual(globalAccountStatus.current, AccountStatus.PartiallyCreated)
      ) {
        // PARTIALLY_CREATED fallback: redirect with the URL captured while polling.
        if (partiallyCreatedUrl.current) {
          onSuccessCallback(
            partiallyCreatedUrl.current,
            AccountStatus.PartiallyCreated,
          );
        } else {
          onFailureCallback(GET_ACCOUNT_STATUS_FAILURE);
        }
      } else {
        onFailureCallback(ACCOUNT_CREATION_STATUS_TIMEOUT);
      }
    }, pollingIntervalLength);
  };

  const accountStatusHandler = useCallback(
    (accountStatus: AccountStatus, url = '') => {
      switch (accountStatus) {
        case AccountStatus.Pending:
          // Non-terminal: keep polling.
          break;
        case AccountStatus.PartiallyCreated:
          // Non-terminal: track for the timeout fallback and capture the url.
          globalAccountStatus.current = accountStatus;
          if (url) {
            partiallyCreatedUrl.current = url;
          }
          break;
        case AccountStatus.Created:
          if (!url) {
            if (claimCompletion()) {
              console.error(NO_URL_FOUND_ERROR);
              onFailureCallback(GET_ACCOUNT_STATUS_URL_MISSING);
            }
            return;
          }
          if (claimCompletion()) {
            onSuccessCallback(url, AccountStatus.Created);
          }
          break;
        case AccountStatus.Disabled:
          if (claimCompletion()) {
            onFailureCallback(GET_ACCOUNT_STATUS_DISABLED);
          }
          break;
        default:
          if (claimCompletion()) {
            console.error(ACCOUNT_TYPE_MISMATCH_ERROR);
            onFailureCallback(GET_ACCOUNT_STATUS_TYPE_MISMATCH);
          }
          break;
      }
    },
    // eslint-disable-next-line
    [],
  );

  useEffect(() => {
    if (accountStatusData?.accountStatus) {
      accountStatusHandler(
        accountStatusData.accountStatus,
        accountStatusData.url,
      );
    }
  }, [accountStatusData]);

  return {
    pollAccountStatus,
  };
};

export default useAccountStatus;
